// Client for the project monitoring endpoints
// (apiv3 /api/3/org/{org}/projects/{project}/monitoring/...).
import { API, HOST } from '@/utils/http'
import { buildOrderTileUrl } from '@/lib/tileUrl'

const base = (orgId, projectId) => `${HOST}/org/${orgId}/projects/${projectId}/monitoring`

export async function fetchMonitoringSettings(orgId, projectId) {
  const { data } = await API.get(`${base(orgId, projectId)}/settings`)
  return data
}

export async function updateMonitoringSettings(orgId, projectId, update) {
  const { data } = await API.put(`${base(orgId, projectId)}/settings`, update)
  return data
}

export async function fetchMonitoringStatus(orgId, projectId) {
  const { data } = await API.get(`${base(orgId, projectId)}/status`)
  return data
}

export async function fetchFeatureMonitoring(orgId, projectId, featureCode) {
  const { data } = await API.get(`${base(orgId, projectId)}/features/${featureCode}/monitoring`)
  return data
}

export async function enableFeatureMonitoring(orgId, projectId, featureCode) {
  const { data } = await API.post(`${base(orgId, projectId)}/features/${featureCode}/monitoring`)
  return data
}

export async function disableFeatureMonitoring(orgId, projectId, featureCode) {
  const { data } = await API.delete(`${base(orgId, projectId)}/features/${featureCode}/monitoring`)
  return data
}

// { metric, resolution, available: [res], points: [{date, mean}], features: {code: latestMean} }
// features param: comma-separated feature codes; empty = all monitored features (area-weighted).
export async function fetchMetricSeries(orgId, projectId, metric, { features, resolution, dateFrom, dateTo } = {}) {
  const params = {}
  if (features) params.features = features
  if (resolution) params.resolution = resolution
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  const { data } = await API.get(`${base(orgId, projectId)}/metrics/${metric}`, { params })
  return data
}

// { metric, resolution, storage_bucket, storage_prefix, max_zoom, render_domain, frames: [{date, result_id}] }
export async function fetchFeatureFrames(orgId, projectId, featureCode, metric, resolution) {
  const params = resolution ? { resolution } : {}
  const { data } = await API.get(`${base(orgId, projectId)}/features/${featureCode}/frames/${metric}`, { params })
  return data
}

// { captures: [{kind, item_date, planet_order_id, product_bundle, status, feature_code, feature_name, ...}] }
export async function fetchCaptures(orgId, projectId) {
  const { data } = await API.get(`${base(orgId, projectId)}/captures`)
  return data
}

// { alerts: [...], unread }
// feature param: a feature code; empty = all monitored features.
export async function fetchAlerts(orgId, projectId, category, feature) {
  const params = {}
  if (category) params.category = category
  if (feature) params.feature = feature
  const { data } = await API.get(`${base(orgId, projectId)}/alerts`, { params })
  return data
}

export async function markAlertsRead(orgId, projectId) {
  const { data } = await API.post(`${base(orgId, projectId)}/alerts/read`)
  return data
}

// Tile URL for a completed capture — same resolution logic as every other
// order layer (direct S3 when storage pointers exist, tulum proxy otherwise).
export function captureTileUrl(orgId, capture) {
  return buildOrderTileUrl(orgId, capture.layer_code, capture, capture.product_bundle)
}
